import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';

import { fadeInEnter } from '@libs/src/animations/animations';
import { Memoize } from '@libs/src/functions/memoize';
import {
  goalNameLimit,
  insightPreferencesOptions,
  role,
} from '@libs/src/goal/account-goal.helper';
import {
  AccountGoal,
  GoalMemberSegment,
} from '@libs/src/models/account-goal.model';
import {
  Account,
  AccountGroupMembership,
} from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import { getMembership } from '@main-client/src/app/account/account.helpers';

import find from 'lodash-es/find';
import get from 'lodash-es/get.js';
import includes from 'lodash-es/includes';
import some from 'lodash-es/some';

@Component({
  animations: [fadeInEnter],
  providers: [DestroySubscriptionsService],
  selector: 'app-group-account-goals',
  standalone: false,
  templateUrl: './group-account-goals.component.html',
})
export class GroupAccountGoalsComponent implements OnChanges {
  @Input() user: Account;
  @Input() group: Group;
  @Output() goalFormsValid = new EventEmitter();
  accountGoals: AccountGoal[] = [];
  accountGoalsForms: UntypedFormArray = this.fb.array([]);
  accountGroupMembership: AccountGroupMembership;

  constructor(
    public destroy$: DestroySubscriptionsService,
    public fb: UntypedFormBuilder,
  ) {}

  ngOnChanges() {
    this.accountGroupMembership = getMembership(
      this.user.groups,
      this.group._id,
    );
    this.accountGoals = get(
      this.accountGroupMembership,
      'goals',
      [],
    ) as AccountGoal[];
    this.accountGoalsForms = this.fb.array([]);
    this.accountGoals.forEach((accountGoal: AccountGoal) => {
      this.accountGoalsForms.push(this.initializeAccountGoalForm(accountGoal));
    });
    if (!this.accountGoalsForms.value.length) {
      this.addNewAccountGoal();
    }
    const areAnyFormsInEditMode = some(
      this.accountGoalsForms.controls,
      (control: AbstractControl) => control.get('isEditMode').value,
    );
    this.goalFormsValid.emit(
      !areAnyFormsInEditMode && this.accountGoalsForms.valid,
    );
  }

  addNewAccountGoal() {
    this.accountGoalsForms.push(
      this.fb.group({
        goal_areas: [[], Validators.required],
        goal_name: [
          '',
          [Validators.required, Validators.maxLength(goalNameLimit)],
        ],
        insight_preferences: [{}, Validators.required],
        isEditMode: [true, Validators.required],
        member_segments: this.fb.array([]),
      }),
    );
  }

  deleteAccountGoalForm(goalIndex: number) {
    this.accountGoalsForms.removeAt(goalIndex);
    if (!this.accountGoalsForms.controls.length) {
      this.addNewAccountGoal();
    }
  }

  @Memoize()
  getFormGroup(control: AbstractControl) {
    return control as UntypedFormGroup;
  }

  initializeAccountGoalForm(goal: AccountGoal) {
    const insightsPreference = find(insightPreferencesOptions, {
      value: goal.insight_preferences,
    });
    const goalAreas = goal.goal_areas.map((goalArea) => ({
      _id: goalArea,
      name: goalArea,
    }));
    return this.fb.group({
      goal_areas: [goalAreas || [], Validators.required],
      goal_name: [
        goal.goal_name || '',
        [Validators.required, Validators.maxLength(goalNameLimit)],
      ],
      insight_preferences: [insightsPreference || '', Validators.required],
      isEditMode: [false, Validators.required],
      member_segments: this.fb.array([
        ...this.initializeGoalMemberSegmentsForm(goal, this.group),
      ]),
    });
  }

  initializeGoalMemberSegmentsForm(goal: AccountGoal, group: Group) {
    return goal.member_segments
      .filter(
        (memberSegment: GoalMemberSegment) =>
          find(group.custom_segments, {
            _id: memberSegment.segment_id,
          }) || role === memberSegment.segment_type,
      )
      .map((memberSegment: GoalMemberSegment) => {
        if (role === memberSegment.segment_type) {
          const selectedRoles = group.roles
            .filter(({ _id }) => includes(memberSegment.segment_items, _id))
            .map(({ _id, name }) => ({
              _id,
              name,
            }));
          return this.fb.group({
            nodes: [selectedRoles, Validators.required],
            segment: [
              { _id: role, name: $localize`:@@global.role:Role` },
              Validators.required,
            ],
          });
        }
        const customSegment = find(group.custom_segments, {
          _id: memberSegment.segment_id,
        });
        const selectedNodes = customSegment?.nodes
          .filter(({ _id }) => includes(memberSegment.segment_items, _id))
          .map(({ _id, name }) => ({
            _id,
            name,
          }));
        return this.fb.group({
          nodes: [selectedNodes, Validators.required],
          segment: [
            { _id: customSegment?._id, name: customSegment?.title },
            Validators.required,
          ],
        });
      });
  }

  @Memoize()
  getGoalForForm({
    accountGoals,
    goalControl,
  }: {
    accountGoals: AccountGoal[];
    goalControl: AbstractControl;
  }) {
    const goalForm = goalControl as UntypedFormGroup;
    return find(accountGoals, {
      goal_name: goalForm.get('goal_name').value,
    });
  }
}
